import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { FolderKanban, MessageSquare, ChevronRight, Clock } from 'lucide-react'

const ClientDashboard = () => {
  const { profile, getDisplayName } = useAuth()
  const [progetti, setProgetti] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.id) return
    const fetch = async () => {
      const { data } = await supabase
        .from('project_members')
        .select('progetti(id, titolo, descrizione, status, created_at)')
        .eq('user_id', profile.id)
      setProgetti((data || []).map(d => d.progetti).filter(Boolean))
      setLoading(false)
    }
    fetch()
  }, [profile?.id])

  const displayName = getDisplayName()
  const ora = new Date().getHours()
  const saluto = ora < 12 ? 'Buongiorno' : ora < 18 ? 'Buon pomeriggio' : 'Buonasera'

  return (
    <div className="max-w-3xl">
      {/* Benvenuto */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">{saluto}, {displayName} 👋</h1>
        <p className="text-gray-500 text-sm mt-1">
          Qui trovi i progetti condivisi con te da Plutonic Studio.
        </p>
      </div>

      {/* Card info */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-violet-600/20 rounded-xl flex items-center justify-center">
              <FolderKanban size={18} className="text-violet-400" />
            </div>
            <span className="text-gray-400 text-sm">Progetti attivi</span>
          </div>
          <p className="text-3xl font-bold text-white">{progetti.length}</p>
        </div>
        <Link to="/client/chat"
          className="bg-gray-900 border border-gray-800 rounded-2xl p-5 hover:border-gray-700 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-violet-600/20 rounded-xl flex items-center justify-center">
              <MessageSquare size={18} className="text-violet-400" />
            </div>
            <span className="text-gray-400 text-sm">Messaggi</span>
          </div>
          <p className="text-sm text-violet-400 font-medium">Apri chat →</p>
        </Link>
      </div>

      {/* Progetti */}
      <div>
        <h2 className="text-sm text-gray-500 uppercase tracking-wider font-medium mb-3">I tuoi progetti</h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : progetti.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
            <FolderKanban size={32} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Nessun progetto ancora condiviso con te.</p>
            <p className="text-gray-600 text-xs mt-1">Il team di Plutonic Studio ti aggiungerà ai progetti che ti riguardano.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {progetti.map(p => (
              <div key={p.id} className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 hover:border-gray-700 transition-colors">
                <div className="w-10 h-10 bg-violet-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FolderKanban size={18} className="text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{p.titolo}</p>
                  {p.descrizione && <p className="text-gray-500 text-xs mt-0.5 truncate">{p.descrizione}</p>}
                  <div className="flex items-center gap-1 mt-1">
                    <Clock size={11} className="text-gray-600" />
                    <span className="text-gray-600 text-xs">
                      {new Date(p.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full flex-shrink-0 ${
                  p.status === 'attivo' ? 'bg-emerald-500/10 text-emerald-400' :
                  p.status === 'completato' ? 'bg-blue-500/10 text-blue-400' :
                  'bg-gray-800 text-gray-500'
                }`}>
                  {p.status || 'In corso'}
                </span>
                <ChevronRight size={16} className="text-gray-600 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientDashboard
